import { Component } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { take, finalize } from 'rxjs';
import { AnalisesProjetoService } from '../../services/analises-projeto/analises-projeto.service';
import { ProjetosService } from '../../services/projetos/projetos.service';
import { PageSizeService } from '../../services/page-size/page-size.service';
import { ToastService } from '../../services/toast/toast.service';
import { LoadingService } from '../loading/loading.service';
import { ModalFiltersComponent } from '../../shared/modals/modal-filters/modal-filters.component';
import { AprovarAnaliseComponent } from '../../shared/modals/aprovar-analise/aprovar-analise.component';
import { ModalResponsavelComponent } from '../../shared/modals/modal-responsavel/modal-responsavel.component';
import { Status } from '../../shared/interface/status-filter.interface';
import { AuthService } from '../../services/auth/auth.service';
import { UserService } from '../../services/user/user.service';

@Component({
  selector: 'app-analises-projeto',
  templateUrl: './analises-projeto.component.html',
  styleUrls: ['./analises-projeto.component.scss'],
})
export class AnalisesProjetoComponent {
  activeTab = 'Funcional';
  tabs = [
    { label: 'Funcional', value: 'Funcional' },
    { label: 'Executivo', value: 'Executivo' },
  ];

  get activeFrom(): string {
    return this.activeTab === 'Executivo' ? 'analises-projeto-executivo' : 'analises-projeto-funcional';
  }

  private readonly colsFuncional = [
    { columnDef: 'contrato', header: 'Contrato', property: 'contrato', customTd: true },
    { columnDef: 'obra', header: 'Código da Obra', property: 'codigoObra', customTd: true },
    { columnDef: 'prazo', header: 'Prazo de análise', property: 'prazo', customTd: true },
    { columnDef: 'responsavel', header: 'Responsável', property: 'responsavel', customTd: true },
    { columnDef: 'statusAtual', header: 'Status', property: 'statusAtual', customTd: true },
    { columnDef: 'actions', header: 'Ações', custom: true },
  ];

  private readonly colsExecutivo = [
    { columnDef: 'contrato', header: 'Contrato', property: 'contrato', customTd: true },
    { columnDef: 'obra', header: 'Código da Obra', property: 'codigoObra', customTd: true },
    { columnDef: 'prazo', header: 'Prazo de análise', property: 'prazo', customTd: true },
    { columnDef: 'responsavel', header: 'Responsável', property: 'responsavel', customTd: true },
    { columnDef: 'statusAtual', header: 'Status', property: 'statusAtual', customTd: true },
  ];

  get columnConfig() {
    return this.activeTab === 'Executivo' ? this.colsExecutivo : this.colsFuncional;
  }

  displayedColumns: string[] = [];

  sortState = {
    sortProperty: 'prazo',
    sortDirection: 'asc',
  };

  private readonly criterioIdField: { [key: string]: string } = {
    contratoDisplay: 'idContrato',
    obraDisplay: 'idObra',
    responsavelDisplay: 'idResponsavel',
  };
  criteriosLabels: { [key: string]: string } = {
    contratoDisplay: 'Contrato',
    obraDisplay: 'Obra',
    responsavelDisplay: 'Responsável',
  };

  dataSource = new MatTableDataSource<any>();
  pageIndex = 0;
  pageSize = 10;
  totalPages = 1;
  totalElements = 0;
  filterParametro = '';
  filterData?: FormGroup;
  criterios: any[] = [];
  listaStatus: Status[] = [];
  statusFilter: any;
  isSoloConcessionaria: boolean = false;

  constructor(
    private analisesProjetoService: AnalisesProjetoService,
    private projetosService: ProjetosService,
    private loadingService: LoadingService,
    private pageSizeService: PageSizeService,
    private toastService: ToastService,
    public dialog: MatDialog,
    private router: Router,
    private authService: AuthService,
    private userService: UserService,
  ) {}

  ngOnInit(): void {
    this.displayedColumns = this.columnConfig.map((col) => col.columnDef);
    this.getFilterOptions();
    this.loadUserInfo();
  }

  loadUserInfo(): void {
    const userId = this.authService.getUserIdFromToken();
    if (!userId) return;

    this.userService.getMenuUsuario(userId).subscribe((response: any) => {
      const tiposEntidade: any[] = response.tiposEntidade || [];
      this.isSoloConcessionaria =
        tiposEntidade.length > 0 &&
        tiposEntidade.every((t: any) => t.tipoEnum === 'CNC');
    });
  }

  getFilterOptions(): void {
    this.analisesProjetoService.getFiltrosStatus().pipe(take(1)).subscribe({
      next: (resp: any) => {
        this.listaStatus = resp;
        this.statusFilter = this.listaStatus.filter((item) => item.selecionado);
        this.getData();
      },
      error: () => this.getData(),
    });
  }

  onTabChange(tab: string): void {
    this.activeTab = tab;
    this.displayedColumns = this.columnConfig.map((col) => col.columnDef);
    this.pageIndex = 0;
    this.getData(0);
  }

  getData(pageIndex: number = 0): void {
    const idContrato = this.filterData?.value?.idContrato || null;
    const idObra = this.filterData?.value?.idObra || null;
    const idResponsavel = this.filterData?.value?.idResponsavel || null;
    const prazoInicio = this.filterData?.value?.prazoInicio || null;
    const prazoFim = this.filterData?.value?.prazoFim || null;
    const foraDoPrazo = this.filterData?.value?.foraDoPrazo ?? null;

    const request$ = this.activeTab === 'Executivo'
      ? this.analisesProjetoService.getListaExecutivo(this.pageSize, pageIndex, this.sortState.sortDirection, this.sortState.sortProperty, this.filterParametro, idContrato, idObra, idResponsavel, this.statusFilter, prazoInicio, prazoFim, foraDoPrazo)
      : this.analisesProjetoService.getListaFuncional(this.pageSize, pageIndex, this.sortState.sortDirection, this.sortState.sortProperty, this.filterParametro, idContrato, idObra, idResponsavel, this.statusFilter, prazoInicio, prazoFim, foraDoPrazo);

    request$
      .pipe(
        take(1),
        finalize(() => this.loadingService.hideLoading()),
      )
      .subscribe({
        next: (res: any) => {
          const { content, pageSize, totalPages, totalElements, pageNumber } = res;
          this.dataSource.data = (content || []).map((item: any) => ({ ...item, id: item.idProjetoServicoAmpliacao }));
          this.pageSize = pageSize;
          this.totalPages = totalPages;
          this.totalElements = totalElements;
          this.pageIndex = pageNumber;
        },
        error: (erro: any) => this.handleRequestError(erro),
      });
  }

  filterStatusChanged(event: {}): void {
    this.statusFilter = event;
    this.getData();
  }

  onSortChange(sortProperty: string): void {
    if (this.sortState.sortProperty === sortProperty) {
      this.sortState.sortDirection = this.sortState.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortState.sortProperty = sortProperty;
      this.sortState.sortDirection = 'asc';
    }
    this.getData(0);
  }

  pageChangeEvent(event: PageEvent): void {
    const { pageIndex, pageSize } = event;

    if (this.pageSize !== pageSize) {
      this.pageSize = pageSize;
      this.pageSizeService.setPageSize(pageSize);
      this.getData(0);
      return;
    }

    if (this.pageIndex !== pageIndex) {
      this.getData(pageIndex);
    }
  }

  applyFilter(filterValue: string): void {
    this.filterParametro = filterValue;
    this.getData();
  }

  openModalFilters(): void {
    const dialogRef = this.dialog.open(ModalFiltersComponent, {
      panelClass: ['filter-panel'],
      data: { type: 'analisesProjeto', propsAnalisesProjeto: this.filterData },
    });

    dialogRef.afterClosed().subscribe({
      next: (data: any) => {
        if (data) {
          this.filterData = data;
          this.criterios = this.getCriterios(data);
          this.getData();
        }
      },
    });
  }

  private generatePropsFromForm(form: FormGroup): any {
    const props: any = {};
    Object.keys(this.criterioIdField).forEach((key) => {
      const control = form.get(key);
      if (control && control.value) {
        props[key] = control.value;
      }
    });
    return props;
  }

  getCriterios(data: FormGroup): string[] {
    return Object.keys(this.generatePropsFromForm(data));
  }

  criterioRemoved(event: any): void {
    const idField = this.criterioIdField[event.criterio];
    this.filterData?.patchValue({
      [event.criterio]: null,
      ...(idField ? { [idField]: null } : {}),
    });
    this.criterios = this.criterios.filter((c: any) => c !== event.criterio);
    this.getData();
  }

  redirectToDetailPage(id: string): void {
    this.router.navigate([`detail-projeto/${id}`]);
  }

  editResponsavel(idAnaliseProjeto: string): void {
    const row = this.dataSource.data.find((item: any) => item.idAnaliseProjeto === idAnaliseProjeto);
    if (!row) return;

    const dialogRef = this.dialog.open(ModalResponsavelComponent, {
      width: '700px',
      data: {
        id: row.idAnaliseProjeto,
        projectId: row.idProjetoServicoAmpliacao,
        responsavel: row.responsavel ?? null,
        prazo: row.prazo ?? null,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.getData(this.pageIndex);
      }
    });
  }

  transitionStatusChanged(event: any): void {
    const row = event.data;
    const transicaoId = event.transicaoId;
    const transicaoNome = event.transicaoNome;
    const isEncerrarAnalise = transicaoNome === 'Encerrar análise';

    const dialogRef = this.dialog.open(AprovarAnaliseComponent, {
      width: '1550px',
      data: {
        idAnaliseAfericao: row.idAnaliseProjeto,
        statusAtualId: row.status?.statusAtual?.id,
        transicaoId,
        transicaoNome,
        isFromProjectDetail: true,
        isEncerrarAnalise,
        mostrarParecer: isEncerrarAnalise,
        responsavel: row.responsavel ?? null,
        cnpj: row.contrato?.cnpj,
      },
    });

    dialogRef.afterClosed().subscribe((formData) => {
      if (formData) {
        this.transitionStatus(row.idProjetoServicoAmpliacao, formData);
      }
    });
  }

  private transitionStatus(idProjeto: string, formData: FormData): void {
    this.loadingService.showLoading();

    this.projetosService
      .postTransicaoStatusAnalise(idProjeto, formData)
      .pipe(
        take(1),
        finalize(() => this.loadingService.hideLoading()),
      )
      .subscribe({
        next: () => {
          this.getData(this.pageIndex);
          this.toastService.showSuccess('Mudança de status realizada com sucesso');
        },
        error: (erro: any) => this.handleRequestError(erro),
      });
  }

  onRowExpanded(row: any): void {
    if (row.disciplinas !== undefined) return;
    this.loadDisciplinas(row);
  }

  private loadDisciplinas(row: any): void {
    row.disciplinas = null;
    this.analisesProjetoService
      .getListaExecutivoDisciplinas(row.idProjetoServicoAmpliacao, 100, 0, 'asc', 'disciplina')
      .pipe(take(1))
      .subscribe({
        next: (res: any) => { row.disciplinas = res.content || []; },
        error: () => { row.disciplinas = []; },
      });
  }

  onAnaliseExecutivoAction(event: { action: string; row: any; disciplina: any; transicaoId?: string; transicaoNome?: string }): void {
    const { action, row, disciplina } = event;
    switch (action) {
      case 'view':
        this.redirectToDetailPage(row.idProjetoServicoAmpliacao);
        break;
      case 'tramitar':
        this.transitionStatusChangedDisciplina(row, disciplina, event.transicaoId, event.transicaoNome);
        break;
      case 'editar':
        this.editResponsavelDisciplina(row, disciplina);
        break;
    }
  }

  private editResponsavelDisciplina(row: any, disciplina: any): void {
    const dialogRef = this.dialog.open(ModalResponsavelComponent, {
      width: '700px',
      data: {
        id: disciplina.idAnaliseProjeto,
        projectId: row.idProjetoServicoAmpliacao,
        disciplinaId: disciplina.idDisciplinaProjeto,
        responsavel: disciplina.responsavel ?? null,
        prazo: disciplina.prazo ?? null,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.loadDisciplinas(row);
      }
    });
  }

  private transitionStatusChangedDisciplina(row: any, disciplina: any, transicaoId?: string, transicaoNome?: string): void {
    const isEncerrarAnalise = transicaoNome === 'Encerrar análise';

    const dialogRef = this.dialog.open(AprovarAnaliseComponent, {
      width: '1550px',
      data: {
        idAnaliseAfericao: disciplina.idAnaliseProjeto,
        statusAtualId: disciplina.status?.statusAtual?.id,
        transicaoId,
        transicaoNome,
        isFromProjectDetail: true,
        isDisciplina: true,
        isEncerrarAnalise,
        mostrarParecer: isEncerrarAnalise,
        responsavel: disciplina.responsavel ?? null,
        cnpj: row.contrato?.cnpj,
      },
    });

    dialogRef.afterClosed().subscribe((formData) => {
      if (formData) {
        this.loadingService.showLoading();
        this.projetosService
          .postTransicaoStatusAnaliseDisciplina(row.idProjetoServicoAmpliacao, disciplina.idDisciplinaProjeto, formData)
          .pipe(
            take(1),
            finalize(() => this.loadingService.hideLoading()),
          )
          .subscribe({
            next: () => {
              this.loadDisciplinas(row);
              this.toastService.showSuccess('Mudança de status realizada com sucesso');
            },
            error: (erro: any) => this.handleRequestError(erro),
          });
      }
    });
  }

  private handleRequestError(erro: any): void {
    if (erro.status !== 500 && erro.status) {
      this.toastService.showError(erro.error?.message);
    } else {
      this.toastService.showError();
    }
  }
}
